import { Avatar, AvatarFallback, AvatarImage } from "@/ui-components/ui/avatar";
import { Badge } from "@/ui-components/ui/badge";
import { Button } from "@/ui-components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import { Eye } from "lucide-react";

interface UsersTableProps {
  onUserSelect: (userId: string) => void;
}

export function UsersTable({ onUserSelect }: UsersTableProps) {
  // Mock data - in a real app, this would come from an API
  const users = [
    {
      id: "1",
      name: "Alex Johnson",
      email: "alex@example.com",
      avatar: "/placeholder.svg?height=40&width=40",
      initials: "AJ",
      role: "Vertriebsmitarbeiter",
      status: "active",
      leads: 24,
      sales: 42,
      revenue: "$124,500",
    },
    {
      id: "2",
      name: "Sarah Williams",
      email: "sarah@example.com",
      avatar: "/placeholder.svg?height=40&width=40",
      initials: "SW",
      role: "Vertriebsmitarbeiter",
      status: "active",
      leads: 18,
      sales: 38,
      revenue: "$118,200",
    },
    {
      id: "3",
      name: "Michael Brown",
      email: "michael@example.com",
      avatar: "/placeholder.svg?height=40&width=40",
      initials: "MB",
      role: "Vertriebsleiter",
      status: "active",
      leads: 15,
      sales: 35,
      revenue: "$105,300",
    },
    {
      id: "4",
      name: "Emily Davis",
      email: "emily@example.com",
      avatar: "/placeholder.svg?height=40&width=40",
      initials: "ED",
      role: "Vertriebsmitarbeiter",
      status: "away",
      leads: 12,
      sales: 31,
      revenue: "$92,400",
    },
    {
      id: "5",
      name: "David Wilson",
      email: "david@example.com",
      avatar: "/placeholder.svg?height=40&width=40",
      initials: "DW",
      role: "Vertriebsmitarbeiter",
      status: "inactive",
      leads: 8,
      sales: 28,
      revenue: "$84,600",
    },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500">Aktiv</Badge>;
      case "away":
        return <Badge variant="secondary">Abwesend</Badge>;
      case "inactive":
        return <Badge variant="outline">Inaktiv</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Rolle</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Leads</TableHead>
          <TableHead className="text-right">Verkäufe</TableHead>
          <TableHead className="text-right">Umsatz</TableHead>
          <TableHead className="text-right">Aktionen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => (
          <TableRow key={user.id}>
            <TableCell className="font-medium">
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback>{user.initials}</AvatarFallback>
                </Avatar>
                <div>
                  <div>{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </div>
              </div>
            </TableCell>
            <TableCell>{user.role}</TableCell>
            <TableCell>{getStatusBadge(user.status)}</TableCell>
            <TableCell className="text-right">{user.leads}</TableCell>
            <TableCell className="text-right">{user.sales}</TableCell>
            <TableCell className="text-right">{user.revenue}</TableCell>
            <TableCell className="text-right">
              <Button size="sm" variant="ghost" onClick={() => onUserSelect(user.id)}>
                <Eye className="h-4 w-4 mr-1" />
                Anzeigen
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
